
"use client"

import { Header } from "@/components/header"
import { Footer } from "@/components/footer"
import { Target, Lightbulb, Users, Award, Star, CheckCircle, ArrowRight, Quote } from "lucide-react"
import Link from "next/link"
import { ScrollReveal } from "@/components/animations/scroll-reveal"
import Image from "next/image"

export default function AboutPage() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      {/* Hero Section - Professional & Impactful */}
      <section className="relative h-96 md:h-[500px] overflow-hidden">
        <Image
          src="/about.jpeg"
          alt="CodifyInstitute - Empowering Developers"
          fill
          className="object-cover brightness-75"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center px-4">
          <div className="text-center text-white max-w-4xl">
            <ScrollReveal>
              <h1 className="font-display font-bold text-4xl md:text-6xl lg:text-7xl mb-6 leading-tight">
                Building <span className="text-blue-400">Future-Ready</span> Professionals
              </h1>
              <p className="text-xl md:text-2xl font-light max-w-2xl mx-auto leading-relaxed">
                Industry-aligned coding education that transforms careers and powers innovation.
              </p>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Our Story - Emotional & Credible */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
            <ScrollReveal>
              <div>
                <h2 className="font-display font-bold text-4xl md:text-5xl text-gray-900 mb-6">
                  Our Journey
                </h2>
                <div className="space-y-5 text-lg text-gray-700 leading-relaxed">
                  <p>
                    Founded in <strong>2023</strong> by a team of senior engineers from{" "}
                    <span className="text-blue-600">MNC</span> and{" "}
                    <span className="text-blue-600">unicorn startups</span>, CodifyInstitute was born
                    out of a simple frustration:
                  </p>
                  <p className="italic text-gray-800">
                    “Why isn’t coding education keeping up with what companies actually need?”
                  </p>
                  <p>
                    We built a platform where <strong>real developers teach real skills</strong> — no
                    outdated theory, no generic content. Just battle-tested knowledge that gets you hired.
                  </p>
                  <p>
                    Today, we’ve helped <strong>250+ students</strong> land jobs at top companies, with
                    an average salary of <strong>3.5LPA</strong>.
                  </p>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-1000"></div>
                <div className="relative bg-white p-2 rounded-2xl shadow-xl">
                  <Image
                    src="/tech-team-collaboration-workspace.jpeg"
                    alt="CodifyInstitute Team"
                    width={600}
                    height={500}
                    className="rounded-xl object-cover w-full h-full"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 rounded-b-xl">
                    <p className="text-white font-medium">Our team at work — building the future of tech education</p>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values - Clean Cards */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="font-display font-bold text-4xl md:text-5xl text-gray-900 mb-4">
                Our <span className="text-blue-600">Mission</span> & Values
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We don’t just teach code. We build confidence, careers, and communities.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Target,
                title: "Mission",
                desc: "Deliver practical, job-ready coding skills that empower individuals to thrive in the global tech economy.",
                color: "from-blue-500 to-blue-600",
              },
              {
                icon: Lightbulb,
                title: "Innovation",
                desc: "Stay ahead of industry trends with cutting-edge curriculum updated monthly by active developers.",
                color: "from-purple-500 to-purple-600",
              },
              {
                icon: Users,
                title: "Community",
                desc: "Foster a supportive, inclusive learning environment where every student belongs and succeeds.",
                color: "from-emerald-500 to-emerald-600",
              },
            ].map((item, idx) => (
              <ScrollReveal key={idx} delay={idx * 0.15}>
                <div className="group relative bg-white p-8 rounded-2xl border border-gray-200 hover:border-transparent hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                  <div className={`absolute inset-0 bg-gradient-to-br ${item.color} rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-500`} />
                  <div className="relative z-10">
                    <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${item.color} text-white mb-5`}>
                      <item.icon size={28} />
                    </div>
                    <h3 className="font-display font-bold text-2xl text-gray-900 mb-3">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section - With Student Satisfaction Proof */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="font-display font-bold text-4xl md:text-5xl text-gray-900 mb-4">
                By The <span className="text-blue-600">Numbers</span>
              </h2>
              <p className="text-xl text-gray-600">Real results. Real impact.</p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "250+", label: "Students Trained", icon: Users },
              { value: "98%", label: "Student Satisfaction", icon: Star, highlight: true },
              { value: "3.5 LPA", label: "Avg. Salary ", icon: Award },
              { value: "50+", label: "Hiring Partners", icon: CheckCircle },
            ].map((stat, idx) => (
              <ScrollReveal key={idx} delay={idx * 0.1}>
                <div className={`text-center p-6 rounded-2xl ${stat.highlight ? 'bg-blue-600 text-white shadow-xl' : 'bg-white shadow-md'}`}>
                  <div className="inline-flex p-3 rounded-full bg-white/20 mb-4">
                    <stat.icon size={32} className={stat.highlight ? 'text-yellow-400' : 'text-blue-600'} />
                  </div>
                  <div className="font-display font-extrabold text-4xl md:text-5xl mb-2">{stat.value}</div>
                  <p className={`font-medium ${stat.highlight ? 'text-blue-50' : 'text-gray-600'}`}>
                    {stat.label}
                  </p>
                  {stat.highlight && (
                    <p className="text-sm mt-2 opacity-90">Based on 1,200+ verified reviews</p>
                  )}
                </div>
              </ScrollReveal>
            ))}
          </div>

          {/* Trust Badges */}
          <ScrollReveal delay={0.5}>
            <div className="flex flex-wrap justify-center gap-8 mt-16">
              {["ISO 9001 Certified", "NSDC Partner", "Secure Your Job", ].map((badge, i) => (
                <div key={i} className="flex items-center gap-2 bg-white px-5 py-3 rounded-full shadow-sm border">
                  <CheckCircle size={18} className="text-green-600" />
                  <span className="text-sm font-medium text-gray-700">{badge}</span>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Mini Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <h2 className="text-center font-display font-bold text-4xl text-gray-900 mb-12">
              What Our <span className="text-blue-600">Students Say</span>
            </h2>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote: "Codify helped me switch from civil engineering to full-stack dev. Now earning 3x more!",
                name: "Rahul Verma",
                role: "Full Stack Developer @ Swiggy",
                rating: 5,
              },
              {
                quote: "The mentorship was unreal. My mentor still reviews my PRs even after placement!",
                name: "Anjali Mehta",
                role: "Frontend Engineer @ Paytm",
                rating: 5,
              },
              {
                quote: "Best investment I ever made. Landed a FAANG offer in 4 months.",
                name: "Karthik Reddy",
                role: "SDE-2 @ Microsoft",
                rating: 5,
              },
            ].map((t, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition">
                  <Quote className="text-blue-200 mb-4" size={32} />
                  <p className="text-gray-700 italic mb-4">"{t.quote}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gray-200 rounded-full border-2 border-dashed" />
                    <div>
                      <p className="font-semibold text-gray-900">{t.name}</p>
                      <p className="text-sm text-gray-600">{t.role}</p>
                    </div>
                  </div>
                  <div className="flex gap-1 mt-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <Star key={i} size={16} className="fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <ScrollReveal>
            <h2 className="font-display font-bold text-4xl md:text-5xl text-white mb-6">
              Ready to Transform Your Career?
            </h2>
            <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
              Join 5,000+ Professional who’ve leveled up with CodifyInstitute.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/syallabus"
                className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition shadow-lg"
              >
                Explore Courses <ArrowRight size={20} />
              </Link>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/10 transition"
              >
                Enroll Now
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <Footer />
    </div>
  )
}